// src/components/pages/OIMonitoring.js
import React, { useState, useEffect } from "react";
import {
  Eye,
  Play,
  Square,
  RefreshCw,
  Search,
  Filter,
  AlertTriangle,
  Send,
} from "lucide-react";

const LoadingProgress = ({
  isLoading,
  progress = 0,
  message = "Loading...",
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAnimatedProgress(0);
    }
  }, [isLoading, progress]);

  if (!isLoading) return null;

  return (
    <div
      style={{
        padding: "60px 20px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: "48px",
          marginBottom: "24px",
          opacity: 0.7,
          animation: "pulse 2s infinite",
        }}
      >
        📊
      </div>

      <h3
        style={{
          fontSize: "18px",
          marginBottom: "16px",
          color: "var(--text-primary)",
        }}
      >
        {message}
      </h3>

      {/* Progress Bar Container */}
      <div
        style={{
          width: "100%",
          maxWidth: "400px",
          margin: "0 auto 16px",
          background: "var(--tertiary-dark)",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid var(--border-color)",
        }}
      >
        <div
          style={{
            height: "8px",
            background:
              "linear-gradient(90deg, var(--accent-gold), var(--accent-green))",
            width: `${animatedProgress}%`,
            transition: "width 0.3s ease-out",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Progress Text */}
      <div
        style={{
          fontSize: "14px",
          color: "var(--text-secondary)",
          marginBottom: "8px",
        }}
      >
        {Math.round(animatedProgress)}% Complete
      </div>

      {/* Loading Dots */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "4px",
          marginTop: "16px",
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: "var(--accent-gold)",
              animation: `loadingDot 1.4s infinite ease-in-out both`,
              animationDelay: `${i * 0.16}s`,
            }}
          />
        ))}
      </div>

      <style jsx>{`
        @keyframes loadingDot {
          0%,
          80%,
          100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

const OIMonitoring = ({ oiMonitor, showToast }) => {
  const [config, setConfig] = useState({
    changeThreshold: 10,
    volumeThreshold: 20,
    interval: 5,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("oi");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("Initializing...");

  // Sync with oiMonitor config
  useEffect(() => {
    if (oiMonitor.config) {
      setConfig(oiMonitor.config);
    }
  }, [oiMonitor.config]);

  // Simulate loading progress when monitoring starts
  useEffect(() => {
    if (
      oiMonitor.isMonitoring &&
      (!oiMonitor.marketData || oiMonitor.marketData.length === 0)
    ) {
      let progress = 0;
      const messages = [
        "Connecting to Binance API...",
        "Loading trading pairs...",
        "Fetching Open Interest data...",
        "Analyzing market data...",
        "Detecting anomalies...",
      ];

      const progressTimer = setInterval(() => {
        progress += Math.random() * 15 + 5; // Random increment between 5-20

        if (progress >= 100) {
          progress = 100;
          setLoadingMessage("Data loading complete");
          clearInterval(progressTimer);
        } else {
          const messageIndex = Math.floor((progress / 100) * messages.length);
          setLoadingMessage(
            messages[Math.min(messageIndex, messages.length - 1)]
          );
        }

        setLoadingProgress(progress);
      }, 500);

      return () => clearInterval(progressTimer);
    } else {
      setLoadingProgress(0);
      setLoadingMessage("Initializing...");
    }
  }, [oiMonitor.isMonitoring, oiMonitor.marketData]);

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    oiMonitor.updateConfig?.(newConfig);
  };

  const handleToggleMonitoring = async () => {
    try {
      if (oiMonitor.isMonitoring) {
        oiMonitor.stop();
        showToast("info", "OI Monitor", "Monitoring stopped");
      } else {
        setLoadingProgress(0);
        setLoadingMessage("Starting monitor...");
        await oiMonitor.start();
        showToast("success", "OI Monitor", "Monitoring started");
      }
    } catch (error) {
      showToast("error", "Monitor Error", error.message);
    }
  };

  const handleRefresh = async () => {
    try {
      setLoadingProgress(0);
      setLoadingMessage("Refreshing data...");
      await oiMonitor.refreshData();
      showToast("success", "Data Refreshed", "OI monitoring data updated");
    } catch (error) {
      showToast("error", "Refresh Failed", error.message);
    }
  };

  const handleDiscordSetup = () => {
    const currentWebhook = localStorage.getItem("discordWebhook") || "";
    const newWebhook = prompt(
      "Enter your Discord webhook URL:",
      currentWebhook
    );
    if (newWebhook !== null) {
      if (newWebhook.trim() === "") {
        localStorage.removeItem("discordWebhook");
        showToast("info", "Discord Setup", "Discord webhook removed");
      } else if (newWebhook.includes("discord.com/api/webhooks/")) {
        localStorage.setItem("discordWebhook", newWebhook.trim());
        showToast(
          "success",
          "Discord Setup",
          "Discord webhook configured successfully"
        );
      } else {
        showToast(
          "error",
          "Discord Setup",
          "Invalid webhook URL. Must be a Discord webhook URL."
        );
      }
    }
  };

  const handleTestDiscord = async () => {
    try {
      const success = await oiMonitor.testDiscordWebhook?.();
      if (success) {
        showToast("success", "Discord Test", "Test message sent successfully!");
      } else {
        showToast("error", "Discord Test", "Failed to send test message");
      }
    } catch (error) {
      showToast("error", "Discord Test", error.message);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(1) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
    return num?.toLocaleString() || "0";
  };

  const formatCurrency = (value) => {
    if (value >= 1e9) return (value / 1e9).toFixed(2) + "B";
    if (value >= 1e6) return (value / 1e6).toFixed(2) + "M";
    if (value >= 1e3) return (value / 1e3).toFixed(2) + "K";
    return value?.toLocaleString() || "0";
  };

  // Filter and sort data
  const filteredData =
    oiMonitor.marketData?.filter((item) => {
      const matchesSearch = item.symbol
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "alert" && item.status === "alert") ||
        (statusFilter === "normal" && item.status === "normal");
      return matchesSearch && matchesStatus;
    }) || [];

  const sortedData = [...filteredData].sort((a, b) => {
    let aVal, bVal;
    switch (sortBy) {
      case "symbol":
        aVal = a.symbol;
        bVal = b.symbol;
        break;
      case "change":
        aVal = a.changePercent;
        bVal = b.changePercent;
        break;
      case "deviation":
        aVal = a.deviation;
        bVal = b.deviation;
        break;
      case "oi":
        aVal = a.openInterest;
        bVal = b.openInterest;
        break;
      case "volume":
        aVal = a.volume;
        bVal = b.volume;
        break;
     case "volchange":
        aVal = a.volumeChange
        bVal = b.volumeChange
        break;
      default:
        aVal = a.openInterest;
        bVal = b.openInterest;
    }

    if (typeof aVal === "string") {
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  const alertData = oiMonitor.alertData || [];
  const totalOI =
    oiMonitor.marketData?.reduce(
      (sum, item) => sum + (item.openInterest || 0),
      0
    ) || 0;
  const avgChange = oiMonitor.marketData?.length
    ? oiMonitor.marketData.reduce(
        (sum, item) => sum + Math.abs(item.changePercent),
        0
      ) / oiMonitor.marketData.length
    : 0;

  // Fixed: Find top mover using absolute values but keep original sign for display
  const topMover = oiMonitor.marketData?.length
    ? oiMonitor.marketData.reduce((prev, current) =>
        Math.abs(current.changePercent) > Math.abs(prev.changePercent)
          ? current
          : prev
      )
    : null;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  // Check if we should show loading
  const isLoadingData =
    oiMonitor.isMonitoring &&
    (!oiMonitor.marketData || oiMonitor.marketData.length === 0);

  return (
    <div className="page-section">
      {/* Configuration Panel */}
      <div
        style={{
          background: "var(--secondary-dark)",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
            fontSize: "16px",
            fontWeight: "600",
            color: "var(--text-primary)",
          }}
        >
          <Eye size={16} style={{ color: "var(--accent-gold)" }} />
          Open Interest Monitor Settings
        </div>

        <div
          style={{
            display: "flex",
            gap: "24px",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: "180px",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
                minWidth: "100px",
              }}
            >
              Change Threshold:
            </span>
            <input
              type="number"
              value={config.changeThreshold}
              onChange={(e) =>
                handleConfigChange(
                  "changeThreshold",
                  parseFloat(e.target.value)
                )
              }
              min="1"
              max="100"
              style={{
                background: "var(--tertiary-dark)",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "var(--text-primary)",
                width: "70px",
                fontSize: "14px",
              }}
            />
            <span style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
              %
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: "150px",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
                minWidth: "70px",
              }}
            >
              Interval:
            </span>
            <select
              value={config.interval}
              onChange={(e) =>
                handleConfigChange("interval", parseInt(e.target.value))
              }
              style={{
                background: "var(--tertiary-dark)",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "var(--text-primary)",
                fontSize: "14px",
                minWidth: "80px",
              }}
            >
              <option value={1}>1 min</option>
              <option value={5}>5 min</option>
              <option value={10}>10 min</option>
              <option value={15}>15 min</option>
            </select>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {!oiMonitor.isMonitoring ? (
            <button
              onClick={handleToggleMonitoring}
              className="btn primary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Play size={16} />
              Start Monitor
            </button>
          ) : (
            <button
              onClick={handleToggleMonitoring}
              className="btn danger"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Square size={16} />
              Stop Monitor
            </button>
          )}

          <button
            onClick={handleRefresh}
            className="btn secondary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <RefreshCw size={16} />
            Manual Refresh
          </button>

          <button
            onClick={handleDiscordSetup}
            style={{
              background: "#7289da",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all var(--transition-fast)",
            }}
          >
            Discord Setup
          </button>

          <button
            onClick={handleTestDiscord}
            disabled={!localStorage.getItem("discordWebhook")}
            style={{
              background: localStorage.getItem("discordWebhook")
                ? "#43b581"
                : "#666",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: localStorage.getItem("discordWebhook")
                ? "pointer"
                : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all var(--transition-fast)",
              opacity: localStorage.getItem("discordWebhook") ? 1 : 0.6,
            }}
          >
            <Send size={14} />
            Test Discord
          </button>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "var(--accent-gold)",
              marginBottom: "8px",
            }}
          >
            {formatNumber(totalOI / 1e9)}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: "4px",
            }}
          >
            Total Open Interest (B)
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-muted)",
            }}
          >
            --
          </div>
        </div>

        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "var(--accent-gold)",
              marginBottom: "8px",
            }}
          >
            {alertData.length}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: "4px",
            }}
          >
            Anomalous Pairs
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-muted)",
            }}
          >
            --
          </div>
        </div>

        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "var(--accent-gold)",
              marginBottom: "8px",
            }}
          >
            {avgChange.toFixed(2)}%
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: "4px",
            }}
          >
            Average Change
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-muted)",
            }}
          >
            --
          </div>
        </div>

        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: "var(--accent-gold)",
              marginBottom: "8px",
            }}
          >
            {topMover?.symbol || "--"}
          </div>
          <div
            style={{
              fontSize: "14px",
              color: "var(--text-secondary)",
              marginBottom: "4px",
            }}
          >
            Top Mover
          </div>
          <div
            style={{
              fontSize: "12px",
              color: topMover
                ? topMover.changePercent >= 0
                  ? "var(--accent-green)"
                  : "var(--accent-red)"
                : "var(--text-muted)",
            }}
          >
            {topMover
              ? `${
                  topMover.changePercent > 0 ? "+" : ""
                }${topMover.changePercent.toFixed(2)}%`
              : "--"}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <div
        style={{
          background: "var(--secondary-dark)",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "20px",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "24px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: "1",
              minWidth: "250px",
            }}
          >
            <Search size={16} style={{ color: "var(--text-secondary)" }} />
            <span
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
                minWidth: "50px",
              }}
            >
              Search:
            </span>
            <input
              type="text"
              placeholder="Enter trading pair name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: "var(--tertiary-dark)",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "var(--text-primary)",
                flex: "1",
                fontSize: "14px",
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={16} style={{ color: "var(--text-secondary)" }} />
            <span style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
              Status:
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                background: "var(--tertiary-dark)",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "var(--text-primary)",
                fontSize: "14px",
                minWidth: "80px",
              }}
            >
              <option value="all">All</option>
              <option value="normal">Normal</option>
              <option value="alert">Alert</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", color: "var(--text-secondary)" }}>
              Sort:
            </span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                background: "var(--tertiary-dark)",
                border: "1px solid var(--border-color)",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "var(--text-primary)",
                fontSize: "14px",
                minWidth: "120px",
              }}
            >
              <option value="oi">Open Interest</option>
              <option value="change">Change Rate</option>
              <option value="deviation">Price Deviation</option>
              <option value="volume">Volume</option>
              <option value="symbol">Symbol</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alert Table */}
      {alertData.length > 0 && (
        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--accent-red)",
            borderRadius: "12px",
            marginBottom: "24px",
            overflow: "hidden",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              background: "rgba(246, 70, 93, 0.1)",
              padding: "16px 24px",
              borderBottom: "1px solid var(--accent-red)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={18} style={{ color: "var(--accent-red)" }} />
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "var(--accent-red)",
                }}
              >
                Market Anomaly Detection
              </span>
            </div>
            <span
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
              }}
            >
              {alertData.length} trading pairs
            </span>
          </div>

          <div style={{ padding: "0" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 2fr 1fr",
                gap: "12px",
                padding: "16px 24px",
                fontSize: "12px",
                fontWeight: "600",
                color: "var(--text-secondary)",
                borderBottom: "1px solid var(--border-color)",
                background: "var(--primary-dark)",
              }}
            >
              <div>Symbol</div>
              <div>Open Interest</div>
              <div>Change Rate ↓</div>
              <div>MA Deviation</div>
              <div>Volume</div>
              <div>Volume Change</div>
              <div>Anomaly Reason</div>
              <div>Time</div>
            </div>

            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {alertData.map((item, index) => (
                <div
                  key={item.symbol}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 2fr 1fr",
                    gap: "12px",
                    padding: "16px 24px",
                    borderBottom:
                      index < alertData.length - 1
                        ? "1px solid var(--border-color)"
                        : "none",
                    fontSize: "13px",
                    background: "rgba(246, 70, 93, 0.03)",
                    transition: "background-color var(--transition-fast)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background =
                      "rgba(246, 70, 93, 0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      "rgba(246, 70, 93, 0.03)")
                  }
                >
                  <div
                    style={{ fontWeight: "600", color: "var(--text-primary)" }}
                  >
                    {item.symbol}
                  </div>
                  <div style={{ color: "var(--text-primary)" }}>
                    {formatCurrency(item.openInterest)}
                  </div>
                  <div
                    style={{
                      color:
                        item.changePercent >= 0
                          ? "var(--accent-green)"
                          : "var(--accent-red)",
                      fontWeight: "600",
                    }}
                  >
                    {item.changePercent > 0 ? "+" : ""}
                    {item.changePercent.toFixed(2)}%
                  </div>
                  <div style={{ color: "var(--text-primary)" }}>
                    {item.deviation.toFixed(2)}%
                  </div>
                  <div style={{ color: "var(--text-primary)" }}>
                    {formatCurrency(item.volume)}
                  </div>
                  <div
                    style={{
                      color:
                        item.volumeChange >= 0
                          ? "var(--accent-green)"
                          : "var(--accent-red)",
                      fontWeight: "600",
                    }}
                  >
                    {item.volumeChange > 0 ? "+" : ""}
                    {item.volumeChange.toFixed(2)}%
                  </div>
                  <div style={{ color: "var(--accent-red)", fontSize: "12px" }}>
                    {item.anomalyReason || "High change rate"}
                  </div>
                  <div
                    style={{ color: "var(--text-secondary)", fontSize: "11px" }}
                  >
                    {new Date(item.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Market Data Table */}
      <div
        style={{
          background: "var(--secondary-dark)",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "var(--shadow-sm)",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid var(--border-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--tertiary-dark)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              fontWeight: "600",
              color: "var(--text-primary)",
            }}
          >
            <span style={{ fontSize: "18px" }}>⟨</span>
            Market Data
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            {oiMonitor.isMonitoring
              ? `${sortedData.length} trading pairs (showing all)`
              : 'Click "Start Monitor" to begin real-time analysis'}
          </div>
        </div>

        {/* Show Loading Progress or Empty State */}
        {isLoadingData ? (
          <LoadingProgress
            isLoading={true}
            progress={loadingProgress}
            message={loadingMessage}
          />
        ) : !oiMonitor.marketData || oiMonitor.marketData.length === 0 ? (
          <div
            style={{
              padding: "80px 20px",
              textAlign: "center",
              color: "var(--text-secondary)",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                marginBottom: "16px",
                opacity: 0.5,
              }}
            >
              📊
            </div>
            <h3
              style={{
                fontSize: "18px",
                marginBottom: "8px",
                color: "var(--text-primary)",
              }}
            >
              Monitoring not started
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
              }}
            >
              Click "Start Monitor" to begin real-time analysis
            </p>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr",
                gap: "12px",
                padding: "16px 24px",
                background: "var(--primary-dark)",
                fontSize: "12px",
                fontWeight: "600",
                color: "var(--text-secondary)",
                borderBottom: "1px solid var(--border-color)",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("symbol")}
              >
                Symbol
                {sortBy === "symbol" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("oi")}
              >
                Open Interest
                {sortBy === "oi" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("change")}
              >
                Change Rate
                {sortBy === "change" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("deviation")}
              >
                Price Deviation
                {sortBy === "deviation" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("volume")}
              >
                Volume
                {sortBy === "volume" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                onClick={() => handleSort("volchange")}
              >
                Volume Change
                {sortBy === "volchange" && (
                  <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                )}
              </div>
              <div>Update Time</div>
              <div>Status</div>
            </div>

            {/* Table Body */}
            <div style={{ maxHeight: "600px", overflowY: "auto" }}>
              {sortedData.map((item, index) => (
                <div
                  key={item.symbol}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr",
                    gap: "12px",
                    padding: "16px 24px",
                    borderBottom: "1px solid var(--border-color)",
                    background:
                      item.status === "alert"
                        ? "rgba(246, 70, 93, 0.05)"
                        : "transparent",
                    borderLeft:
                      item.status === "alert"
                        ? "3px solid var(--accent-red)"
                        : "3px solid transparent",
                    fontSize: "13px",
                    alignItems: "center",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => {
                    if (item.status !== "alert") {
                      e.currentTarget.style.background = "var(--hover-bg)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (item.status !== "alert") {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <div
                    style={{ fontWeight: "600", color: "var(--text-primary)" }}
                  >
                    {item.symbol}
                  </div>

                  <div
                    style={{
                      color: "var(--text-primary)",
                      fontFamily: "monospace",
                    }}
                  >
                    {formatCurrency(item.openInterest)}
                  </div>

                  <div
                    style={{
                      color:
                        item.changePercent >= 0
                          ? "var(--accent-green)"
                          : "var(--accent-red)",
                      fontWeight: "600",
                      fontFamily: "monospace",
                    }}
                  >
                    {item.changePercent > 0 ? "+" : ""}
                    {item.changePercent.toFixed(2)}%
                  </div>

                  <div
                    style={{
                      color:
                        item.deviation >= 0
                          ? "var(--accent-green)"
                          : "var(--accent-red)",
                      fontFamily: "monospace",
                    }}
                  >
                    {item.deviation > 0 ? "+" : ""}
                    {item.deviation.toFixed(2)}%
                  </div>

                  <div
                    style={{
                      color: "var(--text-primary)",
                      fontFamily: "monospace",
                    }}
                  >
                    {formatCurrency(item.volume)}
                  </div>

                  <div
                    style={{
                      color:
                        item.volumeChange >= 0
                          ? "var(--accent-green)"
                          : "var(--accent-red)",
                      fontFamily: "monospace",
                      fontWeight: "600",
                    }}
                  >
                    {item.volumeChange > 0 ? "+" : ""}
                    {item.volumeChange.toFixed(2)}%
                  </div>

                  <div
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "11px",
                      fontFamily: "monospace",
                    }}
                  >
                    {new Date(item.timestamp).toLocaleTimeString()}
                  </div>

                  <div>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontSize: "10px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        background:
                          item.status === "alert"
                            ? "var(--accent-red)"
                            : "var(--accent-green)",
                        color: "white",
                        animation:
                          item.status === "alert"
                            ? "pulse 2s infinite"
                            : "none",
                      }}
                    >
                      {item.status === "alert" ? "Alert" : "Normal"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Monitoring Status Footer with Countdown */}
      {oiMonitor.isMonitoring && (
        <div
          style={{
            background: "var(--secondary-dark)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "16px 24px",
            marginTop: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                background: "var(--accent-green)",
                boxShadow: "0 0 8px rgba(2, 192, 118, 0.5)",
                animation: "pulse 2s infinite",
              }}
            ></div>
            <span
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "var(--text-primary)",
              }}
            >
              Monitoring Active
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
              }}
            >
              Interval: {config.interval} minutes
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
            }}
          >
            {oiMonitor.nextScanCountdown > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "var(--tertiary-dark)",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                  }}
                >
                  Next scan in:
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "var(--accent-gold)",
                    fontFamily: "monospace",
                    minWidth: "45px",
                  }}
                >
                  {oiMonitor.formatCountdown?.(oiMonitor.nextScanCountdown) ||
                    "--:--"}
                </span>
              </div>
            )}
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
              }}
            >
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes loadingDot {
          0%,
          80%,
          100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default OIMonitoring;
